'use client';

// Генератор-чат (лид-магнит). Поведение 1:1 из docs/mockups/demo-flow.html:
// ввод (текст/голос) → «думаю…» → маркетинговая схема (БЕЗ сборки JSON).
// «Принять» ведёт на тарифы (acceptHref). Двуязычность — через dict (приходит из next-intl).
// Голос — общий useVoiceInput; вью ленты — chat-view.tsx, вью схемы — schema-view.tsx.
import { useEffect, useRef, useState } from 'react';
import styles from './generator.module.css';
import { MicIcon, SendIcon } from './icons';
import { ChatMessages, type BotMsg, type Msg, type UserMsg } from './chat-view';
import { type GeneratorDict, type SchemaNode } from './schema';
import { useVoiceInput } from './use-voice-input';

export type { GeneratorDict } from './schema';

export interface GeneratorChip {
  label: string;
  text: string;
}

// История для бэка (диалог): роли user/assistant.
interface ApiMsg {
  role: 'user' | 'assistant';
  content: string;
}
type GenResult =
  | { kind: 'ask'; message: string; options?: string[] }
  | { kind: 'schema'; nodes: SchemaNode[] }
  | { kind: 'error' };

export interface GeneratorExample {
  category: string; // локализованное имя категории (чипсы)
  text: string; // локализованный текст примера
}

interface Props {
  dict: GeneratorDict;
  chips?: GeneratorChip[];
  // Примеры-категории под инпутом: клик по категории подставляет случайный пример.
  examples?: GeneratorExample[];
  acceptHref: string;
  // Белый знак-логотип для аватара бота (на акцентном фоне).
  markSrc?: string;
  // URL генератора (Проход-1). Если задан — шлём реальный запрос; иначе/при ошибке —
  // локальная демо-схема (заглушка). POST {prompt} → { ok, nodes: SchemaNode[] }.
  generateUrl?: string;
  // URL подбора лучшей модели для AI-шага. POST {operation, options} → { ok, model, reason }.
  pickModelUrl?: string;
  // URL распознавания речи (микрофон). POST {audioB64, mime} → { ok, text }.
  transcribeUrl?: string;
  // URL примеров-категорий (по локали). GET → { ok, examples: GeneratorExample[] }.
  examplesUrl?: string;
  // URL записи лид-черновика. POST {prompt, schema} → сохраняем для рассылки/кабинета.
  leadUrl?: string;
  // URL записи полного диалога (UI-снимок messages). POST {messages, firstPrompt} —
  // upsert по anon-cookie; админка показывает диалог 1-в-1.
  dialogUrl?: string;
  // Статичный список категорий (чипсы) — рендерится сразу, без прыжка. Тексты
  // примеров под них подгружаются по examplesUrl и подставляются по клику.
  categories?: string[];
}

export function Generator({
  dict,
  chips = [],
  examples: examplesProp = [],
  acceptHref,
  // Новый знак (владелец 10.08.2026): скруглённый квадрат #feb3c8 с тёмной молнией.
  // Прежний круг #e23570 отменён и удалён из public/brand — сюда его не возвращать.
  markSrc = '/logo-192.png',
  generateUrl,
  pickModelUrl,
  transcribeUrl,
  examplesUrl,
  leadUrl,
  dialogUrl,
  categories = [],
}: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [examples, setExamples] = useState<GeneratorExample[]>(examplesProp);
  const [value, setValue] = useState('');
  const [started, setStarted] = useState(false);
  const [sending, setSending] = useState(false); // идёт produce() — блокирует повторную отправку
  const [micErr, setMicErr] = useState(false);
  const idRef = useRef(0);
  const apiHistory = useRef<ApiMsg[]>([]); // диалог для бэка (user/assistant)
  // id ТЕКУЩЕЙ беседы для записи диалога: одна беседа = одна строка в БД
  // (upsert по паре anon-cookie + этот ref). Обнуляется в reset() — «заново»
  // и перезагрузка страницы дают новую строку, старая не затирается.
  const dialogRef = useRef<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const lastBotRef = useRef<HTMLDivElement | null>(null);
  const nextId = () => (idRef.current += 1);

  const { recording, transcribing, vizRef, toggle } = useVoiceInput({
    transcribeUrl,
    onText: (text) => {
      setValue((v) => (v ? v + ' ' : '') + text);
      requestAnimationFrame(resize);
      taRef.current?.focus();
    },
    onError: () => setMicErr(true),
  });

  // Подтягиваем примеры-категории (по локали) с бэка.
  useEffect(() => {
    if (!examplesUrl) return;
    let alive = true;
    fetch(examplesUrl)
      .then((r) => r.json())
      .then((j) => {
        if (alive && j?.ok && Array.isArray(j.examples)) setExamples(j.examples as GeneratorExample[]);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [examplesUrl]);

  // После каждого изменения чата позиционируем скролл:
  // новый ответ бота — его ВЕРХ к верху окна (читаем с начала, не уезжаем в середину);
  // сообщение юзера / «думаю…» — к низу, чтобы было видно.
  useEffect(() => {
    const c = chatRef.current;
    if (!c || messages.length === 0) return;
    const last = messages[messages.length - 1];
    requestAnimationFrame(() => {
      const bot = lastBotRef.current;
      if (last?.role === 'bot' && bot) {
        // Позиция бота относительно скролл-контейнера (без опоры на offsetParent).
        const delta = bot.getBoundingClientRect().top - c.getBoundingClientRect().top;
        c.scrollTop = Math.max(0, c.scrollTop + delta - 12);
      } else {
        c.scrollTop = c.scrollHeight;
      }
    });
  }, [messages]);

  // Захват полного диалога для админки: шлём снимок messages на dialogUrl (upsert по
  // anon-cookie) СРАЗУ после реплики юзера, не дожидаясь ответа бота, — иначе люди,
  // у которых генератор упал или кто закрыл вкладку до ответа, пропадают бесследно.
  // Транзиентное (typing/picking) вычищает сервер. Debounce 800мс схлопывает пачки
  // обновлений (pickModels правит узлы по одному); keepalive — чтобы запрос дожил
  // при закрытии вкладки. Ошибка генерации тоже попадает в снимок (ask-баббл с текстом).
  useEffect(() => {
    if (!dialogUrl || !messages.some((m) => m.role === 'user')) return;
    const t = setTimeout(() => {
      if (!dialogRef.current) {
        dialogRef.current =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      }
      const firstPrompt = messages.find((m): m is UserMsg => m.role === 'user')?.text ?? '';
      void fetch(dialogUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ messages, firstPrompt, sessionRef: dialogRef.current }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [messages, dialogUrl]);

  function resize() {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }

  // Пересчитываем высоту textarea после ЛЮБОГО изменения текста (в т.ч. после
  // распознавания речи, когда textarea заново монтируется) — иначе высота не
  // соответствует содержимому, пока не нажмёшь клавишу.
  useEffect(() => {
    if (!recording) resize();
  }, [value, recording, transcribing]);

  function fill(text: string) {
    setValue(text);
    requestAnimationFrame(() => {
      resize();
      taRef.current?.focus();
    });
  }

  // Внешняя подстановка текста (раздел «Примеры» лендинга шлёт событие и скроллит к форме).
  useEffect(() => {
    const onFill = (e: Event) => {
      const text = (e as CustomEvent<{ text?: string }>).detail?.text;
      if (typeof text === 'string' && text) fill(text);
    };
    window.addEventListener('scriptera:generator-fill', onFill);
    return () => window.removeEventListener('scriptera:generator-fill', onFill);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fill стабильна по смыслу (setValue+refs)
  }, []);

  // Категории-чипсы: клик подставляет СЛУЧАЙНЫЙ пример из категории; каждый клик — новый
  // (не повторяем предыдущий подряд).
  // Категории статичны (из пропа) → рисуются сразу. Если не заданы — берём из примеров.
  const exampleCats = categories.length > 0 ? categories : [...new Set(examples.map((e) => e.category))];
  const lastPickRef = useRef<Record<string, number>>({});
  function fillCategory(cat: string) {
    const pool = examples.filter((e) => e.category === cat);
    if (pool.length === 0) return;
    let idx = Math.floor(Math.random() * pool.length);
    if (pool.length > 1 && idx === lastPickRef.current[cat]) idx = (idx + 1) % pool.length;
    lastPickRef.current[cat] = idx;
    fill(pool[idx]!.text);
  }

  // Один ход диалога: шлём всю историю, получаем «вопрос» или «схему».
  // При ошибке/недоступности — честная ошибка (НЕ показываем фейковую схему).
  async function produce(): Promise<GenResult> {
    if (!generateUrl) return { kind: 'error' };
    try {
      const res = await fetch(generateUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: apiHistory.current }),
      });
      const j = (await res.json()) as {
        ok?: boolean;
        action?: 'ask' | 'schema';
        message?: string;
        options?: string[];
        nodes?: SchemaNode[];
      };
      if (j?.ok && j.action === 'ask' && j.message) {
        const options = Array.isArray(j.options)
          ? j.options.filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
          : undefined;
        return { kind: 'ask', message: j.message, options: options?.length ? options : undefined };
      }
      if (j?.ok && j.action === 'schema' && Array.isArray(j.nodes) && j.nodes.length > 0) {
        return { kind: 'schema', nodes: j.nodes };
      }
    } catch {
      /* падаем в ошибку ниже */
    }
    return { kind: 'error' };
  }

  async function send(forced?: string) {
    if (sending) return; // produce() уже в полёте — Enter/клик не плодят второй запрос
    const text = (forced ?? value).trim();
    if (!text) return;
    const firstSend = !started;
    if (firstSend) setStarted(true);
    setSending(true);
    apiHistory.current = [...apiHistory.current, { role: 'user', content: text }];

    // Первое сообщение: чат раскрывается по классу .show (анимация max-height),
    // затем плавно скроллим страницу, чтобы блок встал по центру.
    if (firstSend) {
      setTimeout(() => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 540);
    }

    const userId = nextId();
    const typingId = nextId();
    setMessages((m) => [
      ...m,
      { id: userId, role: 'user', text },
      { id: typingId, role: 'typing' },
    ]);
    setValue('');
    requestAnimationFrame(resize);

    const result = await produce();
    setSending(false);
    if (result.kind === 'error') {
      // не пишем в историю диалога — это наш сбой; даём повторить
      setMessages((m) =>
        m.filter((x) => x.id !== typingId).concat({ id: nextId(), role: 'ask', text: dict.error }),
      );
      requestAnimationFrame(() => taRef.current?.focus());
      return;
    }
    if (result.kind === 'ask') {
      apiHistory.current = [...apiHistory.current, { role: 'assistant', content: result.message }];
      setMessages((m) =>
        m
          .filter((x) => x.id !== typingId)
          .concat({ id: nextId(), role: 'ask', text: result.message, options: result.options }),
      );
      requestAnimationFrame(() => taRef.current?.focus());
      return;
    }
    // схема: сохраняем её в историю (как assistant), чтобы уточнения учитывали контекст
    apiHistory.current = [
      ...apiHistory.current,
      { role: 'assistant', content: JSON.stringify({ nodes: result.nodes }) },
    ];
    const botId = nextId();
    // AI-шаги помечаем «подбираю модель…» — рекомендацию подтянем параллельно
    const nodes = result.nodes.map((n) =>
      n.choose && n.choose.length > 0 && pickModelUrl ? { ...n, picking: true } : n,
    );
    setMessages((m) => {
      const isUpdate = m.some((x) => x.role === 'bot');
      const bot: BotMsg = { id: botId, role: 'bot', isUpdate, nodes, showActions: true };
      return m.filter((x) => x.id !== typingId).concat(bot);
    });
    if (pickModelUrl) void pickModels(botId, result.nodes);
    // Пишем лид-черновик (анонимно): исходный запрос + получившаяся схема — чтобы
    // после регистрации/оплаты знать, что человек хотел.
    if (leadUrl) {
      const firstReq = apiHistory.current.find((m) => m.role === 'user')?.content ?? text;
      void fetch(leadUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt: firstReq, schema: result.nodes }),
      }).catch(() => {});
    }
    // Схема — самый высокий блок: после её отрисовки окно «вырастает», поэтому
    // ещё раз плавно ставим композер по центру экрана.
    requestAnimationFrame(() =>
      setTimeout(
        () => composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        60,
      ),
    );
  }

  // Параллельно по всем AI-шагам: спросить лучшую модель и обновить узел (sel + reason).
  // Кроме самого шага передаём весь контекст: исходную задачу (реплики пользователя)
  // и схему целиком — иначе подбор не знает критических требований («без швов» и т.п.).
  // При ОДНОМ кандидате запрос всё равно уходит: флаг ours (модель есть в Scriptera AI)
  // умеет считать только сервер (каталог шлюза + алиасы), без него шаг ошибочно попадёт
  // в «нужно подключать». Платный LLM сервер в этом случае не зовёт — отвечает сразу.
  async function pickModels(botId: number, nodes: SchemaNode[]) {
    const task = apiHistory.current
      .filter((m) => m.role === 'user')
      .map((m) => m.content)
      .join('\n');
    const scheme = nodes.map((x) => x.t).join(' → ');
    await Promise.all(
      nodes.map(async (n, idx) => {
        if (!n.choose || n.choose.length === 0 || !pickModelUrl) return;
        try {
          const res = await fetch(pickModelUrl, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              operation: `${n.t}. ${n.s}`,
              requirement: n.req,
              task,
              scheme,
              options: n.choose,
            }),
          });
          const j = (await res.json()) as { ok?: boolean; model?: string; reason?: string; ours?: boolean };
          updateNode(botId, idx, j?.ok && j.model ? { picking: false, sel: j.model, reason: j.reason, ours: j.ours } : { picking: false });
        } catch {
          updateNode(botId, idx, { picking: false });
        }
      }),
    );
  }

  function updateNode(botId: number, idx: number, patch: Partial<SchemaNode>) {
    setMessages((m) =>
      m.map((x) =>
        x.id === botId && x.role === 'bot'
          ? { ...x, nodes: x.nodes.map((nd, i) => (i === idx ? { ...nd, ...patch } : nd)) }
          : x,
      ),
    );
  }

  function reset() {
    setMessages([]);
    setStarted(false);
    setValue('');
    apiHistory.current = [];
    dialogRef.current = null; // следующая беседа — новая строка в БД
    // Чат сворачивается анимацией (класс .show снят) → после этого плавно подскроллим
    // к блоку и ставим фокус БЕЗ авто-скролла, чтобы не было рывка.
    setTimeout(() => {
      composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      taRef.current?.focus({ preventScroll: true });
    }, 540);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div>
      <div ref={composerRef} className={styles.composer}>
        {/* chatRef — на скроллере .chatIn (позиционирование скролла ниже по эффекту);
            обёртка .chat анимирует раскрытие grid-рядами (Ф42.3, см. CSS). */}
        <div className={`${styles.chat} ${started ? styles.show : ''}`}>
          <div ref={chatRef} className={styles.chatIn}>
            <div className={styles.chatPad}>
              <ChatMessages
                messages={messages}
                dict={dict}
                markSrc={markSrc}
                acceptHref={acceptHref}
                onOption={(opt) => void send(opt)}
                lastBotRef={lastBotRef}
              />
            </div>
          </div>
        </div>

        <div className={`${styles.prompt} ${recording ? styles.promptRec : ''}`}>
          {recording ? (
            <div ref={vizRef} className={styles.viz} aria-hidden>
              {Array.from({ length: 48 }).map((_, i) => (
                <span key={i} />
              ))}
            </div>
          ) : (
            <textarea
              ref={taRef}
              rows={1}
              value={value}
              disabled={transcribing}
              placeholder={
                transcribing
                  ? dict.micTranscribing
                  : started
                    ? dict.placeholderFollow
                    : dict.placeholder
              }
              onChange={(e) => {
                setValue(e.target.value);
                resize();
              }}
              onKeyDown={onKeyDown}
            />
          )}
          {transcribeUrl && (
            <button
              type="button"
              className={`${styles.iconBtn} ${recording ? styles.rec : ''}`}
              onClick={() => {
                setMicErr(false);
                toggle();
              }}
              disabled={transcribing}
              title={recording ? dict.micStop : dict.micStart}
              aria-label={recording ? dict.micStop : dict.micStart}
            >
              {transcribing ? (
                <span className={styles.spin} aria-hidden />
              ) : recording ? (
                <span className={styles.stopIcon} aria-hidden />
              ) : (
                <MicIcon />
              )}
            </button>
          )}
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.send} ${recording ? styles.hide : ''}`}
            onClick={() => void send()}
            disabled={recording || transcribing || sending || !value.trim()}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>
      {micErr && <div className={styles.micErr}>{dict.micError}</div>}

      {started ? (
        <div className={styles.exList}>
          <button type="button" className={styles.chip} onClick={reset}>
            ↺ {dict.reset}
          </button>
        </div>
      ) : exampleCats.length > 0 ? (
        // Чипсы-категории: клик → случайный пример из категории.
        <div className={styles.exList}>
          {exampleCats.map((cat) => (
            <button
              key={cat}
              type="button"
              className={styles.chip}
              onClick={() => fillCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      ) : !examplesUrl && chips.length > 0 ? (
        // Статичные чипсы — только если категории НЕ подгружаются (иначе мелькали бы
        // старые кнопки до ответа examplesUrl).
        <div className={styles.exList}>
          {chips.map((c) => (
            <button
              key={c.label}
              type="button"
              className={styles.chip}
              onClick={() => fill(c.text)}
            >
              {c.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles.subhint}>{dict.subhint}</div>
    </div>
  );
}
